import React, { useEffect, useState } from 'react';
import { Controlled as CodeMirror } from 'react-codemirror2';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useIntl } from 'react-intl';

import { Button } from '@xsolla/ui-core';

import { useFirstRender } from '../../../utils';
import { CopyButtonWrapper, CopyWrapper, Wrapper } from './styles';

/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-ignore
import('codemirror/lib/codemirror.css');
// @ts-ignore
import('codemirror/theme/material.css');
// @ts-ignore
import('codemirror/mode/htmlmixed/htmlmixed.js');
// @ts-ignore
import('codemirror/mode/javascript/javascript');

type Props = {
  disabled?: boolean;
  initialValue?: string;
  onChange?: (value: string) => void;
  allowCopyCode?: boolean;
  mode?: 'xml' | 'javascript';
  showLineNumbers?: boolean;
  height?: number;
  copyButtonText?: string;
  dataTestId?: string;
};

const CodeEditor = ({
  disabled = false,
  initialValue = '',
  onChange,
  allowCopyCode = false,
  height = 634,
  copyButtonText,
  mode = 'xml',
  showLineNumbers = false,
  dataTestId
}: Props): JSX.Element => {
  const intl = useIntl();
  const [currentValue, setCurrentValue] = useState<string>(initialValue);
  const [copied, setCopied] = useState<boolean>(false);
  const firstRender = useFirstRender();

  useEffect(() => {
    setCurrentValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (!firstRender && copied) {
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [copied]);

  const renderCopyButton = () => (
    <CopyWrapper>
      <CopyButtonWrapper>
        <CopyToClipboard text={currentValue}>
          <Button
            palette="brand"
            size="lg"
            disabled={copied}
            onClick={() => setCopied(true)}
            data-test-id={`${dataTestId}_copy-button`}>
            {copied
              ? intl.formatMessage({ id: 'code-preview.copied' })
              : copyButtonText || intl.formatMessage({ id: 'code-preview.copy' })}
          </Button>
        </CopyToClipboard>
      </CopyButtonWrapper>
    </CopyWrapper>
  );

  return (
    <Wrapper height={height} data-test-id={dataTestId}>
      <CodeMirror
        className="code-preview-wrapper"
        value={currentValue}
        options={{
          readOnly: disabled ? 'nocursor' : false,
          // readOnly: disabled,
          mode: { name: mode, jsonld: true },
          theme: 'material',
          lineNumbers: showLineNumbers
        }}
        onBeforeChange={(editor, data, value) => setCurrentValue(value)}
        onChange={(editor, data, value) => {
          if (onChange) {
            onChange(value);
          }
        }}
      />

      {allowCopyCode && renderCopyButton()}
    </Wrapper>
  );
};

export default CodeEditor;
